import './applicantPreviousResidencePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString } from 'lodash';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    isSyncingResidence,
    previousResidenceCity,
    previousResidenceLineOne,
    previousResidenceLineTwo,
    previousResidencePostalCode,
    previousResidenceState,
    syncingResidenceFailedMessage,
    setPreviousResidenceCity,
    setPreviousResidenceLineOne,
    setPreviousResidenceLineTwo,
    setPreviousResidencePostalCode,
    setPreviousResidenceState,
    syncResidence,
  } = useApplicantStore();

  const {
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      cityValue: previousResidenceCity,
      lineOneValue: previousResidenceLineOne,
      lineTwoValue: previousResidenceLineTwo,
      postalCodeValue: previousResidencePostalCode,
      stateValue: previousResidenceState,
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      cityValue,
      lineOneValue,
      lineTwoValue,
      stateValue,
      postalCodeValue,
    } = values;

    if (!cityValue || !lineOneValue || !stateValue || !postalCodeValue) {
      return;
    }

    setPreviousResidenceCity(cityValue);
    setPreviousResidenceLineOne(lineOneValue);
    setPreviousResidenceLineTwo(lineTwoValue);
    setPreviousResidencePostalCode(postalCodeValue);
    setPreviousResidenceState(stateValue);

    if (isDirty) {
      const synced = await syncResidence();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-previous-residence-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.applicantPreviousResidence.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantPreviousResidence.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantPreviousResidence.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantPreviousResidence.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantPreviousResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.applicantPreviousResidence.form.h1}
              >
                {t('ApplicantPreviousResidencePage.title')}
              </h1>
              <h2
                data-testid={locator.pages.applicantPreviousResidence.form.h2}
              >
                {t('ApplicantPreviousResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingResidenceFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantPreviousResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingResidenceFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.applicantPreviousResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantPreviousResidence.form
                          .addressLine1Input,
                    }}
                    label={t('ApplicantPreviousResidencePage.residenceLineOne')}
                    name="lineOneValue"
                    register={register}
                    setValue={setValue}
                    suggestionFields={{
                      cityFieldName: 'cityValue',
                      stateFieldName: 'stateValue',
                      postalCodeFieldName: 'postalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('lineTwoValue')}
                        autoComplete="address-line2"
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantPreviousResidence.form
                              .addressLine2Input,
                        }}
                        label={t(
                          'ApplicantPreviousResidencePage.residenceLineTwo',
                        )}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('cityValue', cityValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('cityValue')),
                        }}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.cityValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantPreviousResidence.form
                              .cityInput,
                        }}
                        label={t(
                          'ApplicantPreviousResidencePage.residenceCity',
                        )}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.cityValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantPreviousResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.cityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('stateValue', stateValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('stateValue')),
                        }}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        autoComplete="address-level1"
                        error={!isEmpty(errors.stateValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantPreviousResidence.form
                              .stateSelect,
                        }}
                        label={t(
                          'ApplicantPreviousResidencePage.residenceState',
                        )}
                        value={watch('stateValue', previousResidenceState)}
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('ApplicantPreviousResidencePage.residenceState')}
                        </MenuItem>

                        {USStates.map((stateObj) => {
                          return (
                            <MenuItem
                              key={stateObj.abbreviation}
                              value={stateObj.abbreviation}
                            >
                              {stateObj.name}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.stateValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantPreviousResidence.form
                              .stateSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.stateValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('postalCodeValue', postalCodeValidation)}
                        InputLabelProps={{
                          shrink: Boolean(watch('postalCodeValue')),
                        }}
                        autoComplete="postal-code"
                        defaultValue={previousResidencePostalCode}
                        error={!isEmpty(errors.postalCodeValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantPreviousResidence.form
                              .zipCodeInput,
                          inputMode: 'numeric',
                        }}
                        label={t('ApplicantPreviousResidencePage.zip')}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.postalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantPreviousResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.postalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applicantPreviousResidence.form.submitButton
                  }
                  disabled={!isValid}
                  loading={isSyncingResidence}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantResidenceDetailsPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantPreviousResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantPreviousResidencePage';
